import React from 'react';
import classnames from 'classnames';
import { LineChart, Line, Tooltip, Legend, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { CircularProgress } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { SHORT_MONTH_AND_DAY_FORMAT, MONTH_AND_DAY_FORMAT } from '@hydra/common/components/i18n';
import { Translate } from '@hydra/common/components';
import ScoreGraphTooltip from '@hydra/common/components/ScoreGraph/ScoreGraphTooltip';

import scoreToColorCode, { scoreValues } from '@hydra/utils/scoreToColorCode';
import { replaceNullsWithAverage, removeNullValuesFromBeginningAndEnd } from '@hydra/utils/scoreGraphUtils';

import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    marginBottom: theme.spacing(6),
    position: 'relative',
    '& .recharts-legend-item > svg > path': {
      fill: theme.palette.grey[500]
    }
  },
  progressWrapper: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)'
  }
}));

export const renderDot = (props) => {
  const { dataKey, ...rest } = props;
  if (!props.payload.value) {
    return;
  }
  return <circle {...rest} r={2} stroke={scoreToColorCode(props.payload.value)} />;
};

const renderActiveDot = (props) => {
  const { dataKey, ...rest } = props;
  if (!props.payload.value) {
    return;
  }
  return <circle {...rest} r={6} fill={scoreToColorCode(props.payload.value)} />;
};

export const tickFormatter = (tick) => {
  if (tick === 0 || tick === 50 || tick === 100) {
    return tick;
  }
  return '';
};

const renderLegend = (value, entry) => {
  const { color } = entry;
  return <span style={{ color }}><Translate content='dashboard_score_graph_homepage_score' /></span>;
};

const getInnerStops = ({ offset1, offset2, currentValue, nextValue }) => {
  const index1 = scoreValues.findIndex(({ minScoreValue }) => currentValue >= minScoreValue);
  const index2 = scoreValues.findIndex(({ minScoreValue }) => nextValue >= minScoreValue);

  if (Math.abs(index1 - index2) <= 1) {
    return null;
  }
  const stepsCount = Math.abs(index1 - index2);

  const innerStops = [];
  const averageOffset = Math.abs(offset1 - offset2) / stepsCount;
  let currentOffset = offset1;

  let currentColor = scoreToColorCode(currentValue);
  for (let i = 0; i < stepsCount; i++) {
    const nextOffset = currentOffset + averageOffset;

    let nextColor;
    if (index1 > index2) {
      nextColor = scoreValues[index1 - (i + 1)].color;
    } else {
      nextColor = scoreValues[index1 + (i + 1)].color;
    }

    innerStops.push(
      <React.Fragment key={i}>
        <stop offset={`${currentOffset}%`} stopColor={currentColor} />
        <stop offset={`${nextOffset}%`} stopColor={nextColor} />
      </React.Fragment>
    );
    currentOffset = nextOffset;
    currentColor = nextColor;
  }

  return innerStops;
};

export const getLinearGradient = data => {
  const lineParts = [];
  const slicedData = removeNullValuesFromBeginningAndEnd(data);
  const cleanData = replaceNullsWithAverage(slicedData);

  const step = ((cleanData.length * 100) / (cleanData.length - 1)) - 100;
  for (let index = 0; index < cleanData.length - 1; index++) {
    const currentItem = cleanData[index];
    const nextItem = cleanData[index + 1];

    const percentage = ((cleanData.length - index - 1) * 100) / (cleanData.length - 1);
    const offset1 = 100 - percentage;
    const offset2 = offset1 + step;
    const innerStops = getInnerStops({ offset1, offset2, currentValue: currentItem.value, nextValue: nextItem.value });

    lineParts.push(
      <React.Fragment key={index}>
        <stop offset={`${offset1}%`} stopColor={scoreToColorCode(currentItem.value)} />
        {innerStops}
        <stop offset={`${offset2}%`} stopColor={scoreToColorCode(nextItem.value)} />
      </React.Fragment>
    );
  }

  return (
    <defs>
      <linearGradient id='colorScore'>
        {lineParts}
      </linearGradient>
    </defs>
  );
};

const ScoreGraph = ({ data = [], isLoading, ...props }) => {
  const { moment } = useI18n();
  const theme = useTheme();
  const classes = useStyles();
  const { className: classNameProp } = props;

  const shortDateFormatter = (unixTime) => {
    if (!unixTime) {
      return '';
    }
    return moment(unixTime).format(SHORT_MONTH_AND_DAY_FORMAT[moment().locale()]);
  };

  const dateFormatter = (unixTime) => {
    if (!unixTime) {
      return '';
    }
    return moment(unixTime).format(MONTH_AND_DAY_FORMAT[moment().locale()]);
  };

  // edge-case with gradient coordinates,
  // see https://stackoverflow.com/a/21639059/419436
  const allNotNullData = data.filter(({ value }) => typeof value === 'number');
  const allScoreDataEqual = allNotNullData.length > 0 && allNotNullData.every(({ value }) => value === allNotNullData[0].value);

  const isDesktop = () => window.matchMedia(`(min-width: ${theme.breakpoints.values.sm}px)`).matches;

  return (
    <div className={classnames(classes.root, classNameProp)}>
      <ResponsiveContainer height={300}>
        {isLoading
          ? <div className={classes.progressWrapper}><CircularProgress /></div>
          : (
            <LineChart data={data}>
              <CartesianGrid vertical={false} stroke={theme.palette.grey[200]} />
              <XAxis
                dataKey='timestamp'
                tickFormatter={shortDateFormatter}
                tickLine={false}
                padding={{ left: 30, right: 30 }}
                axisLine={{ stroke: theme.palette.grey[200] }}
                interval={isDesktop() ? 5 : 10}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                domain={[0, 100]}
                tickFormatter={tickFormatter}
                width={26}
              />
              {!allScoreDataEqual && getLinearGradient(data)}
              <Tooltip
                filterNull={false}
                labelFormatter={dateFormatter}
                content={<ScoreGraphTooltip />}
              />
              <Legend
                align='left'
                verticalAlign='top'
                height={50}
                iconSize={18}
                payload={[{ type: 'line', color: theme.palette.grey[500] }]}
                formatter={renderLegend}
              />
              <Line
                connectNulls
                type='monotone'
                dataKey='value'
                stroke={allScoreDataEqual && allNotNullData.length > 0 ? scoreToColorCode(allNotNullData[0].value) : 'url(#colorScore)'}
                strokeWidth={3.5}
                dot={renderDot}
                activeDot={renderActiveDot}
              />
            </LineChart>
            )}
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreGraph;
